import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setForm({ current_password: "", new_password: "", confirm: "" });
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (form.new_password !== form.confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated.");
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}
    >
      <DialogContent className="rounded-none" data-testid="change-password-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">
            <ShieldCheck size={12} weight="bold" /> Account
          </div>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">
            Change password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-2">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Current password</Label>
            <Input
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              required
              className="rounded-none border-black/20 h-11"
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">New password</Label>
            <Input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              required
              minLength={6}
              className="rounded-none border-black/20 h-11"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Confirm new password</Label>
            <Input
              type="password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
              minLength={6}
              className="rounded-none border-black/20 h-11"
              data-testid="confirm-password-input"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3" data-testid="change-password-error">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-none bg-[#0B1929] text-white h-11 btn-sharp"
              data-testid="change-password-submit"
            >
              {busy ? "Updating..." : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
